import React from 'react';
import { X, Keyboard, LayoutDashboard, Activity, Ticket, Users, Palette, HelpCircle } from 'lucide-react';

const SHORTCUT_GROUPS = [
  {
    title: 'Navigation',
    icon: LayoutDashboard,
    shortcuts: [
      { keys: 'Ctrl+1', label: 'Dashboard & Statistic' },
      { keys: 'Ctrl+2', label: 'Add Activity' },
      { keys: 'Ctrl+3', label: 'Activities' },
      { keys: 'Ctrl+4', label: 'Report Activity' },
      { keys: 'Ctrl+5', label: 'New Tickets' },
      { keys: 'Ctrl+6', label: 'Tickets' },
      { keys: 'Ctrl+7', label: 'Report Tickets' },
      { keys: 'Ctrl+8', label: 'Master User' },
    ],
    color: 'from-blue-500 to-blue-600',
    bg: 'bg-blue-50',
    text: 'text-blue-600',
  },
  {
    title: 'Actions',
    icon: HelpCircle,
    shortcuts: [
      { keys: 'Ctrl+0', label: 'Cycle theme (Ocean → Emerald → ...)' },
      { keys: 'Ctrl+Shift+/', label: 'Show this help modal' },
    ],
    color: 'from-purple-500 to-purple-600',
    bg: 'bg-purple-50',
    text: 'text-purple-600',
  },
];

const ShortcutsHelpModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto animate-fade-in"
      onClick={onClose}
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />

        <div
          className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-auto z-10 animate-scale-in overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                <Keyboard className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Keyboard Shortcuts</h3>
                <p className="text-sm text-gray-500">Press the key combinations to navigate quickly</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Shortcut Groups */}
          <div className="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
            {SHORTCUT_GROUPS.map((group) => (
              <div key={group.title}>
                <div className="flex items-center space-x-2 mb-3">
                  <div className={`p-1.5 rounded-lg bg-gradient-to-br ${group.color} text-white`}>
                    <group.icon className="w-3.5 h-3.5" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900">{group.title}</h4>
                </div>

                <div className="space-y-1">
                  {group.shortcuts.map((shortcut) => (
                    <div
                      key={shortcut.keys}
                      className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-sm text-gray-600">{shortcut.label}</span>
                      <kbd className="inline-flex items-center px-2 py-1 text-[11px] font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded-lg shadow-sm">
                        {shortcut.keys.split('+').map((part, i) => (
                          <span key={i}>
                            {i > 0 && <span className="mx-0.5 text-gray-400">+</span>}
                            <span className={
                              part === 'Ctrl' ? 'text-blue-600 font-bold' :
                              /^\d+$/.test(part) ? 'text-gray-900' : 'text-gray-700'
                            }>
                              {part}
                            </span>
                          </span>
                        ))}
                      </kbd>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50">
            <p className="text-xs text-gray-400 text-center">
              Shortcuts work globally. They are disabled when typing in input fields.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShortcutsHelpModal;
